// src/features/analytics/components/AnalyticsCoverageNotice.tsx

import { Alert, Anchor, Badge, Group, Tooltip } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { useLegacyReportsEligibility } from '@/features/legacy-reports/useLegacyReportsEligibility';
import { formatRelative, formatTimestamp } from '@/shared/utils/date';
import type { UtcRange } from '@/shared/utils/utcRange';
import { AnalyticsUtils } from '../AnalyticsUtils';
import type { AnalyticsCoverage, AnalyticsFreshness } from '../types';

interface AnalyticsCoverageNoticeProps {
  coverage?: AnalyticsCoverage;
  freshness?: AnalyticsFreshness;
  /**
   * The range the caller actually REQUESTED (for the heatmap that is the
   * clamped range, not the page range). The partial banner names it so the
   * text visibly tracks the range control even when coverage is unchanged.
   */
  range: UtcRange;
  /** Shown when the range has no coverage at all. */
  emptyMessage?: string;
}

/**
 * The shared empty/partial/stale strip (plan §"Empty, partial, stale, and
 * provisional states"). Absence of data is never rendered as a measured zero:
 * callers place this above their content and suppress zero-filled bodies when
 * the empty state is active.
 */
export function AnalyticsCoverageNotice({
  coverage,
  freshness,
  range,
  emptyMessage = 'No analytics data for this range.',
}: AnalyticsCoverageNoticeProps) {
  const navigate = useNavigate();
  const { eligible: legacyReportsEligible } = useLegacyReportsEligibility();

  const isEmpty = !coverage || freshness?.state === 'empty';

  if (isEmpty) {
    return (
      <Alert color="gray" icon={<IconInfoCircle size={16} />}>
        {emptyMessage}
      </Alert>
    );
  }

  const isPartial = coverage.partial;
  const isStale = freshness?.state === 'stale';
  if (!isPartial && !isStale) return null;

  // Point at Legacy Reports only when the uncovered part is the START of the
  // range (history predating collection) — never for a lagging range end.
  const showLegacyPointer =
    isPartial && legacyReportsEligible && AnalyticsUtils.coverageGapAtStart(coverage, range);

  return (
    <Group gap="sm">
      {isPartial && (
        <Alert color="yellow" icon={<IconInfoCircle size={16} />} flex={1}>
          {AnalyticsUtils.partialCoverageMessage(coverage, range)}
          {showLegacyPointer && (
            <>
              {' '}
              Earlier activity is in{' '}
              <Anchor
                component="button"
                type="button"
                size="sm"
                onClick={() => navigate({ to: '/legacy-reports' })}
              >
                Legacy Reports
              </Anchor>
              .
            </>
          )}
        </Alert>
      )}
      {isStale && freshness && (
        <Tooltip label={formatTimestamp(freshness.asOf)}>
          <Badge color="yellow" variant="light">
            Updated {formatRelative(freshness.asOf)}
          </Badge>
        </Tooltip>
      )}
    </Group>
  );
}
